import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CloudService, CloudPageState, CloudSummary, ServiceCardData } from '../../core/services/cloud.service';
import { AwsConnection } from '../../core/services/aws.service';
import { AzureConnection, AzureSubscription } from '../../core/services/azure.service';
import { GcpConnection, GcpProject } from '../../core/services/gcp.service';
import { ALL_REGIONS_SCOPE } from '../../core/aws-regions';
import { locationLabel } from '../../core/azure-locations';
import { regionLabel } from '../../core/gcp-regions';
import { CloudProvider, providerLabel } from '../../core/cloud-catalog';

type AzureOverviewState = 'loading' | 'not-connected' | 'connected' | 'error';
type GcpOverviewState = 'loading' | 'not-connected' | 'connected' | 'error';


/**
 * Cloud overview — the high-level category landing page.
 *
 * Renders the connection summary and the full-width service card grid. Each
 * card is an entry point: clicking it navigates to that service's dedicated
 * detail page (`/cloud/:serviceType`). The gear on a card opens the alerts /
 * ticketing settings drawer (unrelated to the detail navigation).
 */
@Component({
  selector: 'app-cloud-hub-shell',
  templateUrl: './cloud-hub-shell.component.html',
})
export class CloudHubShellComponent implements OnInit, OnDestroy {
  pageState: CloudPageState = 'loading';
  connection: AwsConnection | null = null;
  summary: CloudSummary | null = null;
  drawerService: ServiceCardData | null = null;
  /** True once the subscription's monitoring state has loaded (drives the header badge skeleton). */
  monitoringLoaded = false;

  /** Active cloud provider, bound to the ?provider= query param (default AWS). */
  provider: CloudProvider = 'AWS';

  /** Active AWS account, bound to the ?account= query param. */
  account = '';
  /** Active region scope: a region code or 'all', bound to the ?region= query param (scoped within the account). */
  scope = 'us-east-1';
  readonly ALL_REGIONS = ALL_REGIONS_SCOPE;

  // ── Azure scope — the exact parallel of the AWS account/region scope, bound to
  //    the SAME ?account=/?region= params: ?account= is the Azure subscription id
  //    and ?region= is the Azure location. This keeps one scope model across
  //    providers (provider → scope → geography) with native terminology. ──────
  azureState: AzureOverviewState = 'loading';
  azureConnection: AzureConnection | null = null;
  /** Active Azure subscription (?account=). */
  azureSubscription = '';
  /** Active Azure location scope: a location code or 'all' (?region=). */
  azureLocation = ALL_REGIONS_SCOPE;
  azureMonitoringLoaded = false;
  readonly locationLabel = locationLabel;
  private azureConnSub?: Subscription;

  // ── GCP scope — the exact parallel of the AWS account/region scope, bound to
  //    the SAME ?account=/?region= params: ?account= is the GCP project id and
  //    ?region= is the GCP region. This keeps one scope model across providers
  //    (provider → scope → geography) with native terminology. ───────────────
  gcpState: GcpOverviewState = 'loading';
  gcpConnection: GcpConnection | null = null;
  /** Active GCP project (?account=). */
  gcpProject = '';
  /** Active GCP region scope: a region code or 'all' (?region=). */
  gcpRegion = ALL_REGIONS_SCOPE;
  gcpMonitoringLoaded = false;
  readonly regionLabel = regionLabel;
  private gcpConnSub?: Subscription;

  /** Guards the shared monitoring-state load so it runs once across providers. */
  private monitoringRequested = false;

  /** Card ids whose OWN data is still loading for the current scope (drives the per-card overlay). */
  private readonly loadingCards = new Set<string>();
  /** Card ids whose count is being manually refreshed (drives the per-card refresh spin + double-click guard). */
  readonly refreshingCards = new Set<string>();

  private sub?: Subscription;
  private routeSub?: Subscription;
  /** Per-card initial/scope-change load subscriptions, keyed by card id. */
  private readonly cardLoadSubs = new Map<string, Subscription>();
  private readonly cardRefreshSubs = new Map<string, Subscription>();
  /**
   * Monotonic token for a scope's card loads. Bumped whenever a new scope load
   * starts (account/region/provider change) so a late response from a previous
   * scope is recognised as stale and ignored — it can never patch the wrong scope.
   */
  private loadGeneration = 0;

  readonly skeletonItems = [1, 2, 3];

  constructor(
    readonly cloudService: CloudService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.sub = this.cloudService.getConnectionState().subscribe({
      next: ({ state, connection }) => {
        this.pageState = state;
        this.connection = connection;
        if (state === 'connected' && connection) {
          this.ensureMonitoringLoaded();
          this.cloudService.loadPolicies().subscribe();
          this.cloudService.loadOrgMembers().subscribe();
          // On a live connection refresh (e.g. an account was added or its
          // regions edited on the Integrations page), rebuild the summary so the
          // account/region selectors reflect the change even when the active
          // account/region is unchanged (applyAwsFromRoute would otherwise no-op).
          if (this.provider === 'AWS') {
            if (this.summary) this.summary = this.cloudService.buildSummary(connection);
            this.applyAwsFromRoute();
          }
        }
      },
      error: () => { this.pageState = 'error'; },
    });

    // Azure connection stream — the parallel of the AWS connection above. Owns
    // the Azure connected/not-connected/loading states so the header can render
    // the Azure subscription/location controls and the body its card grid.
    this.azureConnSub = this.cloudService.getAzureConnection().subscribe({
      next: conn => {
        this.azureConnection = conn;
        const active = !!conn && conn.subscriptionCount > 0 && conn.connectionStatus === 'ACTIVE';
        this.azureState = active ? 'connected' : 'not-connected';
        if (active) {
          this.ensureMonitoringLoaded();
          if (this.provider === 'AZURE') this.applyAzureFromRoute();
        }
      },
      error: () => { this.azureState = 'error'; },
    });

    // GCP connection stream — the parallel of the AWS/Azure connection above.
    this.gcpConnSub = this.cloudService.getGcpConnection().subscribe({
      next: conn => {
        this.gcpConnection = conn;
        const active = !!conn && conn.projectCount > 0 && conn.connectionStatus === 'ACTIVE';
        this.gcpState = active ? 'connected' : 'not-connected';
        if (active) {
          this.ensureMonitoringLoaded();
          if (this.provider === 'GCP') this.applyGcpFromRoute();
        }
      },
      error: () => { this.gcpState = 'error'; },
    });

    // React to bookmarkable ?provider= / ?account= / ?region= changes.
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = (params['provider'] as string | undefined)?.toUpperCase();
      this.provider = p === 'AZURE' ? 'AZURE' : p === 'GCP' ? 'GCP' : 'AWS';
      this.cloudService.setProvider(this.provider);
      this.applyScopeFromRoute();
    });
  }

  /** Loads the shared monitoring state (AWS + Azure + GCP entries) exactly once. */
  private ensureMonitoringLoaded(): void {
    if (this.monitoringRequested) return;
    this.monitoringRequested = true;
    this.cloudService.loadMonitoringState().subscribe(() => {
      this.monitoringLoaded = true;
      this.azureMonitoringLoaded = true;
      this.gcpMonitoringLoaded = true;
    });
  }

  /** Applies the active provider's scope from the URL (dispatches by provider). */
  private applyScopeFromRoute(): void {
    if (this.provider === 'AZURE') this.applyAzureFromRoute();
    else if (this.provider === 'GCP') this.applyGcpFromRoute();
    else this.applyAwsFromRoute();
  }

  /** Provider selector → write ?provider= and reset the account/region scope. */
  onProviderChange(provider: CloudProvider): void {
    const providerParam = provider === 'AWS' ? null : provider === 'AZURE' ? 'azure' : 'gcp';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { provider: providerParam, account: null, region: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Human label for the active provider (used in provider-aware copy). */
  get activeProviderLabel(): string { return providerLabel(this.provider); }

  /**
   * Resolves the active account + region from the URL and (re)loads when either
   * changes. Account is resolved first; region is then scoped within that
   * account (falling back to the account's default if the URL region isn't
   * enabled for it). One reload per real change — no double counting.
   */
  private applyAwsFromRoute(): void {
    if (this.pageState !== 'connected' || !this.connection) return;

    const accounts = this.cloudService.getAwsAccounts(this.connection);
    const qpAccount = this.route.snapshot.queryParamMap.get('account');
    const account = (qpAccount && accounts.some(a => a.accountId === qpAccount))
      ? qpAccount
      : (accounts[0]?.accountId ?? '');

    const acct    = accounts.find(a => a.accountId === account);
    const enabled = acct?.enabledRegions ?? [];
    const def     = acct?.defaultRegion ?? 'us-east-1';
    const qpRegion = this.route.snapshot.queryParamMap.get('region');
    const region = (qpRegion && (qpRegion === this.ALL_REGIONS || enabled.includes(qpRegion))) ? qpRegion : def;

    // Correct a stale URL: if the bookmarked ?account=/?region= no longer resolves
    // (e.g. the account was disconnected or the region was removed from its set),
    // rewrite the URL to the value we fell back to. The resulting queryParams
    // change re-enters applyFromRoute with matching params (no loop) and the load
    // happens then.
    if ((qpAccount && qpAccount !== account) || (qpRegion && qpRegion !== region)) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { account, region }, queryParamsHandling: 'merge' });
      return;
    }

    if (account === this.account && region === this.scope && this.summary) return; // nothing changed

    this.account = account;
    this.scope   = region;
    this.cloudService.setAccount(account);
    this.cloudService.setScope(region);
    // buildSummary resets every card to its '—' placeholder, so no stale
    // previous-scope numbers linger while the new scope's cards load in.
    this.summary = this.cloudService.buildSummary(this.connection);
    this.loadServiceMetrics();
  }

  /** Account selector change → write ?account= and reset region (the new account scopes its own regions). */
  onAccountChange(accountId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { account: accountId, region: null }, queryParamsHandling: 'merge' });
  }

  /** Region selector change → write the bookmarkable ?region= param. */
  onScopeChange(scope: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { region: scope }, queryParamsHandling: 'merge' });
  }

  // ── Azure scope (subscription = ?account=, location = ?region=) ─────────────

  /**
   * Resolves the active Azure subscription + location from the URL, mirroring
   * applyAwsFromRoute: subscription first, then location scoped within it
   * (falling back to 'all' enabled locations when the URL location isn't
   * enabled for the subscription). Global services stay in scope regardless.
   */
  private applyAzureFromRoute(): void {
    if (this.azureState !== 'connected' || !this.azureConnection) return;

    const subs = this.azureConnection.subscriptions;
    const qpAccount = this.route.snapshot.queryParamMap.get('account');
    const subscription = (qpAccount && subs.some(s => s.subscriptionId === qpAccount))
      ? qpAccount
      : (subs.find(s => s.isPrimary)?.subscriptionId ?? subs[0]?.subscriptionId ?? '');

    const sub = subs.find(s => s.subscriptionId === subscription);
    const enabled = sub?.enabledLocations ?? [];
    const qpRegion = this.route.snapshot.queryParamMap.get('region');
    const location = (qpRegion && (qpRegion === this.ALL_REGIONS || enabled.includes(qpRegion))) ? qpRegion : this.ALL_REGIONS;

    // Correct a stale bookmarked ?account=/?region= (subscription disconnected or
    // location removed) — rewrite, then re-enter with matching params (no loop).
    if ((qpAccount && qpAccount !== subscription) || (qpRegion && qpRegion !== location)) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { account: subscription, region: location }, queryParamsHandling: 'merge' });
      return;
    }

    this.azureSubscription = subscription;
    this.azureLocation = location;
    // Shared cloud scope drives the Azure card counts + detail fetches. The
    // azure-overview reloads off its subscription/location inputs (set below),
    // by which point the service scope is already correct.
    this.cloudService.setAccount(subscription);
    this.cloudService.setScope(location);
  }

  /** Subscription selector change → write ?account= and reset the location. */
  onAzureSubscriptionChange(subscriptionId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { account: subscriptionId, region: null }, queryParamsHandling: 'merge' });
  }

  /** Location selector change → write the bookmarkable ?region= param. */
  onAzureLocationChange(location: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { region: location }, queryParamsHandling: 'merge' });
  }

  /** Connected Azure subscriptions (drives the subscription selector). */
  get azureSubscriptions(): AzureSubscription[] {
    return this.azureConnection?.subscriptions ?? [];
  }

  get showAzureSubscriptionSwitcher(): boolean {
    return this.azureSubscriptions.length > 1;
  }

  /** Enabled locations of the active Azure subscription (drives the location selector). */
  get azureEnabledLocations(): string[] {
    return this.azureSubscriptions.find(s => s.subscriptionId === this.azureSubscription)?.enabledLocations ?? [];
  }

  get showAzureLocationSwitcher(): boolean {
    return this.azureEnabledLocations.length > 1;
  }

  /** Label of the active Azure subscription (context chip when there's a single subscription). */
  get azureSubscriptionLabel(): string {
    const s = this.azureSubscriptions.find(x => x.subscriptionId === this.azureSubscription)
      ?? this.azureSubscriptions.find(x => x.isPrimary) ?? this.azureSubscriptions[0];
    return s ? (s.label || s.subscriptionId) : '—';
  }

  /** Monitored Azure services for the active subscription + location scope. */
  get azureMonitoredCount(): number {
    return this.cloudService.scopedMonitoredCount(this.azureSubscription, this.azureLocation, 'AZURE');
  }

  /** Connected Azure integration exposed at least one subscription. */
  get hasAzureSubscriptions(): boolean {
    return this.azureState === 'connected' && this.azureSubscriptions.length > 0;
  }

  // ── GCP scope (project = ?account=, region = ?region=) ──────────────────────

  /**
   * Resolves the active GCP project + region from the URL, mirroring
   * applyAzureFromRoute: project first, then region scoped within it (falling
   * back to 'all' enabled regions when the URL region isn't enabled for the
   * project). Global services stay in scope regardless.
   */
  private applyGcpFromRoute(): void {
    if (this.gcpState !== 'connected' || !this.gcpConnection) return;

    const projects = this.gcpConnection.projects;
    const qpAccount = this.route.snapshot.queryParamMap.get('account');
    const project = (qpAccount && projects.some(p => p.projectId === qpAccount))
      ? qpAccount
      : (projects.find(p => p.isPrimary)?.projectId ?? projects[0]?.projectId ?? '');

    const proj = projects.find(p => p.projectId === project);
    const enabled = proj?.enabledRegions ?? [];
    const qpRegion = this.route.snapshot.queryParamMap.get('region');
    const region = (qpRegion && (qpRegion === this.ALL_REGIONS || enabled.includes(qpRegion))) ? qpRegion : this.ALL_REGIONS;

    // Correct a stale bookmarked ?account=/?region= (project disconnected or
    // region removed) — rewrite, then re-enter with matching params (no loop).
    if ((qpAccount && qpAccount !== project) || (qpRegion && qpRegion !== region)) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { account: project, region }, queryParamsHandling: 'merge' });
      return;
    }

    this.gcpProject = project;
    this.gcpRegion = region;
    // Shared cloud scope drives the GCP card counts + detail fetches. The
    // gcp-overview reloads off its project/region inputs (set below), by which
    // point the service scope is already correct.
    this.cloudService.setAccount(project);
    this.cloudService.setScope(region);
  }

  /** Project selector change → write ?account= and reset the region. */
  onGcpProjectChange(projectId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { account: projectId, region: null }, queryParamsHandling: 'merge' });
  }

  /** Region selector change → write the bookmarkable ?region= param. */
  onGcpRegionChange(region: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { region }, queryParamsHandling: 'merge' });
  }

  /** Connected GCP projects (drives the project selector). */
  get gcpProjects(): GcpProject[] {
    return this.gcpConnection?.projects ?? [];
  }

  get showGcpProjectSwitcher(): boolean {
    return this.gcpProjects.length > 1;
  }

  /** Enabled regions of the active GCP project (drives the region selector). */
  get gcpEnabledRegions(): string[] {
    return this.gcpProjects.find(p => p.projectId === this.gcpProject)?.enabledRegions ?? [];
  }

  get showGcpRegionSwitcher(): boolean {
    return this.gcpEnabledRegions.length > 1;
  }

  /** Label of the active GCP project (context chip when there's a single project). */
  get gcpProjectLabel(): string {
    const p = this.gcpProjects.find(x => x.projectId === this.gcpProject)
      ?? this.gcpProjects.find(x => x.isPrimary) ?? this.gcpProjects[0];
    return p ? (p.label || p.projectId) : '—';
  }

  /** Monitored GCP services for the active project + region scope. */
  get gcpMonitoredCount(): number {
    return this.cloudService.scopedMonitoredCount(this.gcpProject, this.gcpRegion, 'GCP');
  }

  /** Connected GCP integration exposed at least one project. */
  get hasGcpProjects(): boolean {
    return this.gcpState === 'connected' && this.gcpProjects.length > 0;
  }

  /** Connected AWS accounts (drives the account selector + the no-accounts empty state). */
  get accounts(): import('../../core/services/aws.service').AwsAccount[] {
    return this.summary?.accounts ?? [];
  }

  /** Whether to surface the account switcher (only meaningful with more than one connected account). */
  get showAccountSwitcher(): boolean {
    return this.accounts.length > 1;
  }

  /** Connected but the integration exposed no AWS accounts — drives a scope-aware empty state. */
  get hasNoAccounts(): boolean {
    return this.pageState === 'connected' && this.accounts.length === 0;
  }

  /** Whether to surface the region switcher (only meaningful with more than one enabled region). */
  get showRegionSwitcher(): boolean {
    return (this.summary?.enabledRegions?.length ?? 0) > 1;
  }

  /** Monitored-services count for the active account + region scope (drives the header badge). */
  get monitoredCount(): number {
    return this.cloudService.scopedMonitoredCount(this.account, this.scope);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.azureConnSub?.unsubscribe();
    this.gcpConnSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.cardLoadSubs.forEach(s => s.unsubscribe());
    this.cardRefreshSubs.forEach(s => s.unsubscribe());
  }

  get serviceCards(): ServiceCardData[] {
    return this.summary?.services ?? [];
  }

  /** True while this card's own data is loading for the current scope (per-card overlay). */
  isCardLoading(cardId: string): boolean {
    return this.loadingCards.has(cardId);
  }

  isRefreshing(cardId: string): boolean {
    return this.refreshingCards.has(cardId);
  }

  /**
   * Manual per-card refresh: bypasses the cache for just this service, fetches a
   * fresh count, and patches only this card. The card stays visible with its
   * previous value while the lightweight refresh spinner runs (no full-card
   * overlay). Guarded against rapid double-clicks while a refresh is in flight,
   * and against a stale response if the scope changes mid-flight.
   */
  refreshCard(card: ServiceCardData): void {
    if (this.refreshingCards.has(card.id)) return;
    const count$ = this.cardCount$(card.id, true);
    if (!count$) return;

    const generation = this.loadGeneration; // scope snapshot at request time
    this.refreshingCards.add(card.id);
    this.cardRefreshSubs.get(card.id)?.unsubscribe();
    this.cardRefreshSubs.set(card.id, count$.subscribe({
      next: count => {
        if (generation !== this.loadGeneration) return; // scope changed — drop stale result
        this.patchCardCount(card.id, count);
        this.refreshingCards.delete(card.id);
      },
      error: () => { this.refreshingCards.delete(card.id); },
    }));
  }

  /** Replaces a single card's count metric, leaving every other card untouched. */
  private patchCardCount(id: string, count: number): void {
    if (!this.summary) return;
    this.summary = {
      ...this.summary,
      services: this.summary.services.map(s =>
        s.id === id ? { ...s, metrics: [{ ...s.metrics[0], value: String(count) }] } : s),
    };
  }

  /** Maps a card id to its resource-count observable (bypass forces a fresh fetch). */
  private cardCount$(id: string, bypass: boolean): Observable<number> | null {
    const len = <T>(o: Observable<T[]>): Observable<number> => o.pipe(map(a => a.length));
    switch (id) {
      case 'lambda':         return len(this.cloudService.getLambdaFunctions(bypass));
      case 'glue':           return len(this.cloudService.getGlueJobs(bypass));
      case 'codepipeline':   return len(this.cloudService.getPipelines(bypass));
      case 'step-functions': return len(this.cloudService.getStateMachines(bypass));
      case 'ecs':            return len(this.cloudService.getEcsServices(bypass));
      case 'rds':            return len(this.cloudService.getRdsInstances(bypass));
      case 'api-gateway':    return len(this.cloudService.getApiGateways(bypass));
      case 'ec2':            return len(this.cloudService.getEc2Instances(bypass));
      case 'cognito':        return len(this.cloudService.getCognitoUserPools(bypass));
      case 'appsync':        return len(this.cloudService.getAppSyncApis(bypass));
      case 'dynamodb':       return len(this.cloudService.getDynamoDbTables(bypass));
      case 'sqs':            return len(this.cloudService.getSqsQueues(bypass));
      case 'eventbridge':    return len(this.cloudService.getEventBridgeRules(bypass));
      case 'alb':            return len(this.cloudService.getAlbLoadBalancers(bypass));
      case 'sns':            return len(this.cloudService.getSnsTopics(bypass));
      case 'cloudfront':     return len(this.cloudService.getCloudFrontDistributions(bypass));
      default:               return null;
    }
  }

  openSettings(card: ServiceCardData): void {
    this.drawerService = card;
  }

  closeSettings(): void {
    this.drawerService = null;
  }

  /**
   * Loads each service card's count INDEPENDENTLY so the grid updates
   * progressively: every card's spinner clears the moment its own response
   * arrives, and one slow service never holds up the others.
   *
   * Concurrency + races:
   *  - A new scope load bumps `loadGeneration` and cancels the previous scope's
   *    in-flight card loads AND refreshes, so their late responses can't patch
   *    the new scope (belt: the generation check also drops any that slip through).
   *  - Each card owns its own subscription; success or error clears only that
   *    card's loading flag.
   */
  private loadServiceMetrics(): void {
    const generation = ++this.loadGeneration;

    // Cancel the previous scope's in-flight work — nothing from it may leak in.
    this.cardLoadSubs.forEach(s => s.unsubscribe());
    this.cardLoadSubs.clear();
    this.cardRefreshSubs.forEach(s => s.unsubscribe());
    this.cardRefreshSubs.clear();
    this.refreshingCards.clear();
    this.loadingCards.clear();

    for (const card of this.summary?.services ?? []) {
      const count$ = this.cardCount$(card.id, false);
      if (!count$) continue;

      this.loadingCards.add(card.id);
      this.cardLoadSubs.set(card.id, count$.subscribe({
        next: count => {
          if (generation !== this.loadGeneration) return; // stale scope — ignore
          this.patchCardCount(card.id, count);
          this.loadingCards.delete(card.id);
        },
        error: () => {
          if (generation !== this.loadGeneration) return;
          // Stop this card's spinner only; it keeps its placeholder and its
          // neighbours are unaffected. (List fetches already fall back to [] on
          // transport errors, so this is a safety net.)
          this.loadingCards.delete(card.id);
        },
      }));
    }
  }
}
